import * as React from 'react';

import {
  Modifier,
  EditorState,
  ContentBlock,
  ContentState,
  SelectionState,
} from 'draft-js';
import {ENITITY_PLACEHOLDER} from './utils';
import {
  cleanPlaceholder,
  PLACEHOLDER_VARIABLE_REGEX,
} from 'src/components/workflow/templates/utils';
import PlaceholderPill from 'src/components/lib/markdown-editor/placeholder-pill';


function findPlaceholderEntities(
  contentBlock: ContentBlock,
  callback: any,
  contentState: ContentState,
) {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey != null &&
      contentState.getEntity(entityKey).getType() === ENITITY_PLACEHOLDER
    );
  }, callback);
}

export const findPlaceholderVariables = (
  contentBlock: ContentBlock,
  callback: (start: number, stop: number, data: unknown) => number,
) => {
  const text = contentBlock.getText();

  const match = PLACEHOLDER_VARIABLE_REGEX.exec(text);
  if (match) {
    const start = match.index;
    const labelProps = match[0];
    callback(start, start + match[0].length, labelProps);
  }
};

const PlaceholderVariable =
  (onReplace: () => void) =>
  // @ts-expect-error - TS7031 - Binding element 'children' implicitly has an 'any' type.
  ({children}) =>
    <PlaceholderPill onReplace={onReplace} text={children} />;

export const PlaceholderDecorator = (onReplace: () => void) => ({
  component: PlaceholderVariable(onReplace),
  strategy: findPlaceholderEntities,
});

const addPlaceholderEntities = (
  contentState: ContentState,
  blockKey: string,
  entries: Array<{
    start: number;
    end: number;
    text: string;
  }>,
) => {
  let newContentState = contentState;
  let currentBlock = contentState.getBlockForKey(blockKey);
  let offset = 0;

  for (const {text, start, end} of entries) {
    newContentState = contentState.createEntity(
      ENITITY_PLACEHOLDER,
      'IMMUTABLE',
      text,
    );

    const entityKey = newContentState.getLastCreatedEntityKey();
    const selectionState = SelectionState.createEmpty(blockKey).merge({
      anchorOffset: start + offset,
      focusOffset: end + offset,
    });

    newContentState = Modifier.replaceText(
      newContentState,
      selectionState,
      cleanPlaceholder(text),
      // @ts-expect-error - TS2345 - Argument of type 'null' is not assignable to parameter of type 'DraftInlineStyle | undefined'.
      null,
      entityKey,
    );

    const nextBlock = newContentState.getBlockForKey(blockKey);
    offset += nextBlock.getLength() - currentBlock.getLength();
    currentBlock = nextBlock;
  }

  return newContentState;
};

/**
 * modifies the given editorState so that any text variables or draft entities
 * show their current Sense Entity display name.
 */
export const getEditorStateWithPlaceholders = (
  editorState: EditorState,
): EditorState => {
  let newEditorState = editorState;
  let contentState = editorState.getCurrentContent();
  let blocks = contentState.getBlocksAsArray();

  contentState = newEditorState.getCurrentContent();
  blocks = contentState.getBlocksAsArray();

  blocks.forEach((block) => {
    const blockKey = block.getKey();

    const placeholderMatches: Array<{
      end: number;
      start: number;
      text: string;
    }> = [];
    const existingPlaceholderEntities: Record<string, any> = {};

    findPlaceholderVariables(block, (start, end, text) =>
      // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string'.
      placeholderMatches.push({start, end, text}),
    );
    const placeholdersToAdd = placeholderMatches.filter(
      ({start, end}) => !existingPlaceholderEntities[`${start}:${end}`],
    );
    const newContentState = addPlaceholderEntities(
      newEditorState.getCurrentContent(),
      blockKey,
      placeholdersToAdd,
    );

    newEditorState = EditorState.push(
      newEditorState,
      newContentState,
      'change-block-data',
    );
  });

  return newEditorState;
};

const getEntities = (editorState: EditorState, entityType = null) => {
  const content = editorState.getCurrentContent();
  const entities: Array<unknown> = [];
  content.getBlocksAsArray().forEach((block) => {
    // @ts-expect-error - TS7034 - Variable 'selectedEntity' implicitly has type 'any' in some locations where its type cannot be determined.
    let selectedEntity = null;
    block.findEntityRanges(
      (character) => {
        if (character.getEntity() !== null) {
          const entity = content.getEntity(character.getEntity() ?? '');
          if (!entityType || (entityType && entity.getType() === entityType)) {
            selectedEntity = {
              entityKey: character.getEntity(),
              blockKey: block.getKey(),
              entity: content.getEntity(character.getEntity() ?? ''),
            };
            return true;
          }
        }
        return false;
      },
      (start, end) => {
        // @ts-expect-error - TS7005 - Variable 'selectedEntity' implicitly has an 'any' type.
        entities.push({...selectedEntity, start, end});
      },
    );
  });
  return entities;
};

export const getPlaceholderEntities = (editorState: EditorState): unknown[] =>
  // @ts-expect-error - TS2345 - Argument of type '"PLACEHOLDER"' is not assignable to parameter of type 'null | undefined'.
  getEntities(editorState, ENITITY_PLACEHOLDER);

export const getPlaceholderEntitiesCount = (editorState: EditorState): number =>
  getPlaceholderEntities(editorState).length;
